import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import React, { useState } from "react";
import { Card, Modal, Button } from "react-bootstrap";
import { useParams } from "react-router-dom";
import NavigateBack from "../../common/NavigateBack";
import { getUserDetails } from "../../services/services";

const Details = () => {
  const IMAGE_BASE_URL = import.meta.env.VITE_DOCUMENT_API_URL || import.meta.env.VITE_API_URL.replace("api/v1", "");

  const { id } = useParams();
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const { data } = useQuery({
    queryKey: ["client-details", id],
    queryFn: async () => {
      const resp = id && (await getUserDetails(id));
      return resp?.data?.data ?? {};
    },
  });

  const getFileName = (doc) => {
    return doc?.fileName || "Unknown Document";
  };

  const getFileType = (filePath) => {
    if (!filePath || typeof filePath !== "string") {
      return "unknown";
    }

    const extension = filePath.split(".").pop()?.toLowerCase();

    if (extension === "pdf") {
      return "application/pdf";
    } else if (extension === "docx") {
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    } else {
      return `image/${extension}`;
    }
  };

  const handlePreview = (doc) => {
    if (doc.filePath && getFileType(doc.filePath) === "application/pdf") {
      const filePath = `${IMAGE_BASE_URL}${doc.filePath}`
      window.open(filePath, "_blank");
      return;
    } else {
      setPreviewDoc(doc);
      setShowPreview(true);
    }
  };

  return (
    <>
      <section className="main-content">
        <NavigateBack>Client Details</NavigateBack>
        <Card border="light" bg="light" className="p-4">
          <div className="clientDetails">
            <div className="clientDetailsContent mb-3">
              <h6 className="heading18 mb-0">Client Name</h6>
              <span className="heading16 ps-2 text-capitalize">{`${data?.firstName} ${data?.lastName}`}</span>
            </div>
            <div className="clientDetailsContent mb-3">
              <h6 className="heading18 mb-0">Date of birth</h6>
              <span className="heading16 ps-2 text-capitalize">
                {data?.dob ? `${moment(data?.dob).format("ll")}` : "N/A"}
              </span>
            </div>
            <div className="clientDetailsContent mb-3">
              <h6 className="heading18 mb-0">Email</h6>
              <span className="heading16 ps-2">{data?.email}</span>
            </div>
            <div className="clientDetailsContent mb-3">
              <h6 className="heading18 mb-0">Phone Number</h6>
              <span className="heading16 ps-2">{data?.phoneNumber}</span>
            </div>
            <div className="clientDetailsContent mb-3">
              <h6 className="heading18 mb-0">Rate</h6>
              <span className="heading16 ps-2">{data?.clientHourlyRate?.toFixed(2) || "N/A"}</span>
            </div>
            <div className="clientDetailsContent mb-3">
              <h6 className="heading18 mb-0">Address</h6>
              <span className="heading16 ps-2">{`${
                data?.userAddress?.address || ""
              } ${data?.userAddress?.city || ""} ${
                data?.userAddress?.state || ""
              } ${data?.userAddress?.country || ""} (${
                data?.userAddress?.zipCode || ""
              })`}</span>
            </div>
            <div className="clientDetailsContent mb-3">
              <h6 className="heading18 mb-0">Note</h6>
              <span className="heading16 ps-2">{`${data?.note || "N/A"}`}</span>
            </div>
            <div className="clientDetailsContent mb-3">
              <h6 className="heading18 mb-0">Authorized Employees</h6>
              <span className="heading16 ps-2">
                {data?.authorizedDetails?.length
                  ? data?.authorizedDetails
                      ?.map((i) => `${i.firstName} ${i.lastName}`)
                      ?.join(", ")
                  : "N/A"}
              </span>
            </div>
            <div className="clientDetailsContent mb-3">
              <h6 className="heading18 mb-0">Authorized Employees (non-automatic)</h6>
              <span className="heading16 ps-2">
                {data?.authorizedNoAutoAssignDetails?.length
                  ? data?.authorizedNoAutoAssignDetails
                      ?.map((i) => `${i.firstName} ${i.lastName}`)
                      ?.join(", ")
                  : "N/A"}
              </span>
            </div>
            <div className="clientDetailsContent mb-3">
              <h6 className="heading18 mb-0">Created At</h6>
              <span className="heading16 ps-2">
                {moment(data?.createdAt).format("lll")}
              </span>
            </div>
            <div className="clientDetailsContent mb-3">
              <h6 className="heading18 mb-0">Documents</h6>
              <span className="heading16 ps-2">
                {data?.documents?.length > 0 ? (
                  <div className="document-list">
                    {data.documents.map((doc, index) => (
                      <div
                        key={doc._id}
                        className="document-item d-flex align-items-center mb-2"
                      >
                        <span
                          className="document-name me-2"
                          style={{ cursor: "pointer" }}
                          onClick={() => handlePreview(doc)}
                        >
                          {getFileName(doc)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  "No documents available"
                )}
              </span>
            </div>
          </div>
        </Card>
      </section>

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Document Preview: {previewDoc && getFileName(previewDoc)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewDoc && (
            <div className="d-flex justify-center">
              {getFileType(previewDoc.filePath) === "application/pdf" ? (
                <a href={`${IMAGE_BASE_URL}${previewDoc.filePath}`} target="_blank" rel="noopener noreferrer">View PDF</a>
              ) : getFileType(previewDoc.filePath) === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ? (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(`${IMAGE_BASE_URL}${previewDoc.filePath}`)}`}
                  width="100%"
                  height="500px"
                  frameBorder="0"
                  title="DOCX Preview"
                />
              ) : ["image/jpeg", "image/png"].includes(getFileType(previewDoc.filePath)) ? (
                <img
                  src={`${IMAGE_BASE_URL}${previewDoc.filePath}`}
                  alt="Document Preview"
                  className="img-fluid mx-auto"
                  style={{ maxWidth: "100%", maxHeight: "500px" }}
                />
              ) : (
                <p>Unsupported file type for preview</p>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Details;